import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PolicyOutlined, Close } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
    setPrivacyPolicyModalOpen,
    openRightSidebar,
    setEditorSelectedOutput,
    closeRightSidebar,
    setSelectedOutputVersion,
    triggerRightSidebarEditor,
} from 'store/global/globalSlice';
import { EDITOR_EMPTY_VALUE } from 'utils/config/constants';
import EditorWithFooter from 'components/Common/EditorWithFooter';
import { getDraftsQuery } from 'api/queries/job';
import { addFirestoreDocs, auth, logEventFirebase, onUpdateDoc } from 'utils/config/firebase';
import { DRAFTS } from 'utils/config/constants/controllerNames';
import { errorLogger } from 'utils/config/firebase/logs';
import { saveDraftSuccess, undoDeletedDraftMessage } from 'helpers/messages';
import { minifyHtml } from 'helpers/functions';
import { DraftType } from '../types';
import FullScreenModal from './FullScreenModal';
import Item from './Item';

type DrawerContentProps = {
    size: number;
};

const DrawerContent = ({ size }: DrawerContentProps) => {
    const dispatch = useAppDispatch();
    const {
        openRightSidebarAutomatically,
        isRightSidebarEditorOpened,
        selectedOutputVersion,
        copiedValue,
    } = useAppSelector((state) => state.global);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const [editorValue, setEditorValue] = useState<string>('');
    const [selectedDraft, setSelectedDraft] = useState<DraftType | null>(null);
    const [drafts, setDrafts] = useState<DraftType[]>([]);
    const { t } = useTranslation();
    const { document_id } = useParams();
    const isEditorOpen = isRightSidebarEditorOpened && !openRightSidebarAutomatically;
    const isListOpen = !isEditorOpen;

    useEffect(() => {
        const unsubscribe = getDraftsQuery(setDrafts);

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, []);

    useEffect(() => {
        if (selectedOutputVersion) {
            setEditorValue(minifyHtml(selectedOutputVersion?.answer));
            setSelectedDraft(null);
        }
    }, [selectedOutputVersion]);

    useEffect(() => {
        if (copiedValue) {
            (async function () {
                try {
                    const clipboardContents = await navigator.clipboard.read();
                    for (const item of clipboardContents) {
                        if (item.types.includes('text/html')) {
                            const blob = await item.getType('text/html');
                            const blobText = await blob.text();
                            setEditorValue((prev) => prev + blobText);
                        } else if (item.types.includes('text/plain')) {
                            const blob = await item.getType('text/plain');
                            const blobText = await blob.text();
                            setEditorValue((prev) => prev + blobText);
                        }
                    }
                } catch (error) {
                    if (error instanceof Error) {
                        errorLogger.mediumError(error?.message, 'copy to clipboard');
                    }
                    console.log(error);
                    setEditorValue((prev) => prev + copiedValue);
                }
            })();
        }
    }, [copiedValue]);

    const toEmptyEditor = () => {
        dispatch(setSelectedOutputVersion(null));
        setEditorValue('');
        setSelectedDraft(null);
    };

    const onOpenPrivacy = () => {
        dispatch(setPrivacyPolicyModalOpen(true));
    };

    const onOpenSidebar = () => {
        dispatch(openRightSidebar());
    };

    const onCloseSidebar = () => {
        if (
            editorValue &&
            editorValue !== EDITOR_EMPTY_VALUE &&
            isRightSidebarEditorOpened &&
            selectedDraft?.value !== editorValue
        ) {
            onSaveDraft(true);
        } else if (isRightSidebarEditorOpened) {
            onTriggerEditor();
        }
        dispatch(setEditorSelectedOutput(''));
        dispatch(closeRightSidebar());
    };

    const onSelectDraft = (draft: DraftType) => {
        setSelectedDraft(draft);
        onTriggerEditor();
        onOpenSidebar();
        setEditorValue(draft.draft_value || draft.value);
    };

    const onOpenFullScreen = () => setIsFullScreen(true);

    const doNothing = () => {
        return true;
    };

    const onSaveDraft = (isDraft = false) => {
        try {
            if (!editorValue) {
                return;
            }
            const clean = DOMPurify.sanitize(editorValue);
            if (selectedDraft) {
                let data = {};
                if (isDraft) {
                    data = {
                        updated_at: new Date().toISOString(),
                        draft_value: clean,
                    };
                } else {
                    data = {
                        updated_at: new Date().toISOString(),
                        value: clean,
                        draft_value: '',
                    };
                }
                logEventFirebase('update_composing_pad_note');
                onUpdateDoc(DRAFTS, selectedDraft.document_id, data);
            } else {
                const data = {
                    version_id: selectedOutputVersion?.document_id ?? null,
                    user_id: auth?.currentUser?.uid,
                    value: isDraft ? '' : clean,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    draft_value: isDraft ? clean : '',
                    deleted: false,
                };
                logEventFirebase('create_composing_pad_note');
                addFirestoreDocs(DRAFTS, data);
            }
            saveDraftSuccess(isDraft);
            handleCloseEditor();
        } catch (error) {
            if (error instanceof Error) {
                errorLogger.critical(error?.message, 'onSaveDraft');
            }
            console.log(error);
        }
    };

    const onDeleteDraft = (document_id: string) => {
        try {
            logEventFirebase('delete_composing_pad_note');
            openUndoMessage(document_id);
            onUpdateDoc(DRAFTS, document_id, {
                deleted: true,
                deleted_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            });
        } catch (error) {
            if (error instanceof Error) {
                errorLogger.critical(error?.message, 'onDeleteDraft');
            }
            console.log(error);
        }
    };

    const onCloseFullScreen = () => setIsFullScreen(false);

    const undoDeletedItem = (document_id: string) => {
        onUpdateDoc(DRAFTS, document_id, {
            deleted: false,
            deleted_at: null,
            updated_at: new Date().toISOString(),
        });
    };

    const openUndoMessage = (document_id: string) => {
        undoDeletedDraftMessage(() => {
            undoDeletedItem(document_id);
        });
    };

    const onTriggerEditor = () => dispatch(triggerRightSidebarEditor());

    const handleEditorChange = (value: string) => {
        setEditorValue(value);
    };

    const handleCloseEditor = (isCanceled = false) => {
        if (selectedDraft?.draft_value && isCanceled) {
            if (selectedDraft.value) {
                onUpdateDoc(DRAFTS, selectedDraft.document_id, { draft_value: '' });
            }
        }
        toEmptyEditor();
        onTriggerEditor();
    };

    return (
        <>
            <Box
                sx={{ width: openRightSidebarAutomatically ? 320 : size }}
                role="presentation"
                className="right-sidebar-open-view"
            >
                <Box className={`right-sidebar-box`}>
                    {document_id ? (
                        <Tooltip placement="top" title={t('web_app.tooltips.right_sidebar_btn')}>
                            <button onClick={onOpenSidebar} className="composing-pad-open-btn">
                                <i className="icon-doc" />
                            </button>
                        </Tooltip>
                    ) : (
                        <div></div>
                    )}
                    <div className="bottom-placement">
                        <Tooltip
                            placement="top"
                            title={t('web_app.home.settings.privacy_policy_and_guidelines')}
                        >
                            <button onClick={onOpenPrivacy} className="privacy-open-btn">
                                <PolicyOutlined />
                            </button>
                        </Tooltip>
                    </div>
                </Box>
                <Box className="right-sidebar-content">
                    <Box className="right-sidebar-open-view-head flex align-center justify-between">
                        <Typography variant="h5">
                            {t('web_app.right_sidebar.title')}
                            {!openRightSidebarAutomatically && (
                                <span>{t('web_app.right_sidebar.subtitle')}</span>
                            )}
                        </Typography>
                        <Box className="flex align-center">
                            <button
                                className="flex align-center justify-center"
                                onClick={onCloseSidebar}
                            >
                                <Close sx={{ fontSize: 24, color: '#000' }} />
                            </button>
                        </Box>
                    </Box>
                    {isEditorOpen ? (
                        <Box className="right-sidebar-open-view-body">
                            <Box className="right-sidebar-editor-container">
                                <Box className="job-generator right-sidebar-editor">
                                    <Box className="output-editor">
                                        <EditorWithFooter
                                            value={editorValue}
                                            handleEditorChange={
                                                !isFullScreen ? handleEditorChange : doNothing
                                            }
                                            hasCancel
                                            onSubmit={onSaveDraft}
                                            onCancel={() => handleCloseEditor(true)}
                                            date={
                                                selectedDraft?.updated_at ??
                                                selectedDraft?.created_at
                                            }
                                            isEditable
                                            onOpenFullScreen={onOpenFullScreen}
                                            title={
                                                selectedOutputVersion?.version
                                                    ? `${t(
                                                          'web_app.right_sidebar.editor_title_2',
                                                      )} ` + selectedOutputVersion?.version
                                                    : ` ${t('web_app.right_sidebar.editor_title')}`
                                            }
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    ) : null}
                    {isListOpen ? (
                        <Box className="right-sidebar-open-view-body">
                            <Box className="right-sidebar-open-view-body-drafts">
                                <Box className="copy-past-button-container">
                                    <button
                                        className="copy-past-button"
                                        onClick={() => {
                                            onTriggerEditor();
                                            onOpenSidebar();
                                        }}
                                    >
                                        {t('web_app.right_sidebar.button_name')}
                                    </button>
                                </Box>
                                <Box className="walkthrough-steps">
                                    <Box>{t('web_app.right_sidebar.description.step_1')}</Box>
                                    <Box>{t('web_app.right_sidebar.description.step_2')}</Box>
                                    <Box>{t('web_app.right_sidebar.description.step_3')}</Box>
                                </Box>
                                <Box className="drafts-container">
                                    {drafts?.map((draft) => (
                                        <Item
                                            onSelectDraft={onSelectDraft}
                                            draft={draft}
                                            key={draft.document_id}
                                            onDeleteDraft={onDeleteDraft}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    ) : null}
                </Box>
            </Box>
            <FullScreenModal
                open={isFullScreen}
                onClose={onCloseFullScreen}
                value={editorValue}
                handleEditorChange={handleEditorChange}
                hasCancel
                onSubmit={() => {
                    onSaveDraft();
                    onCloseFullScreen();
                }}
                onCancel={onCloseFullScreen}
                date={selectedDraft?.updated_at ?? selectedDraft?.created_at}
                isEditable
                title={
                    selectedOutputVersion?.version
                        ? `${t('web_app.right_sidebar.editor_title_2')} ` +
                          selectedOutputVersion?.version
                        : ` ${t('web_app.right_sidebar.editor_title')}`
                }
            />
        </>
    );
};

export default DrawerContent;
